import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, WalletIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { PayoutCard, Payout } from '../components/PayoutCard';
import { EarningCard, Earning } from '../components/EarningCard';
import { AddPayoutModal } from '../components/AddPayoutModal';
import { EditPayoutModal } from '../components/EditPayoutModal';
import { AddEarningModal } from '../components/AddEarningModal';
import { EditEarningModal } from '../components/EditEarningModal';
interface PayoutsProps {
  payouts: Payout[];
  earnings: Earning[];
  onAddPayout: (payout: Omit<Payout, 'id'>) => void;
  onEditPayout: (payout: Payout) => void;
  onDeletePayout: (payout: Payout) => void;
  onAddEarning: (earning: Omit<Earning, 'id'>) => void;
  onEditEarning: (earning: Earning) => void;
  onDeleteEarning: (earning: Earning) => void;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function Payouts({
  payouts,
  earnings,
  onAddPayout,
  onEditPayout,
  onDeletePayout,
  onAddEarning,
  onEditEarning,
  onDeleteEarning,
  customers
}: PayoutsProps) {
  const [isAddPayoutModalOpen, setIsAddPayoutModalOpen] = useState(false);
  const [isAddEarningModalOpen, setIsAddEarningModalOpen] = useState(false);
  const [editingPayout, setEditingPayout] = useState<Payout | null>(null);
  const [editingEarning, setEditingEarning] = useState<Earning | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isReadOnly = false;
  // Filter payouts
  const filteredPayouts = useMemo(() => {
    if (!searchQuery.trim()) return payouts;
    const query = searchQuery.toLowerCase();
    return payouts.filter(payout => payout.notes?.toLowerCase().includes(query) || payout.customerName?.toLowerCase().includes(query));
  }, [payouts, searchQuery]);
  // Filter earnings
  const filteredEarnings = useMemo(() => {
    if (!searchQuery.trim()) return earnings;
    const query = searchQuery.toLowerCase();
    return earnings.filter(earning => earning.notes?.toLowerCase().includes(query) || earning.type.toLowerCase().includes(query) || earning.customerName?.toLowerCase().includes(query));
  }, [earnings, searchQuery]);
  // Calculate stats
  const totalPayouts = payouts.reduce((sum, payout) => sum + payout.amount, 0);
  const totalEarnings = earnings.reduce((sum, earning) => sum + earning.amount, 0);
  const netAmount = totalEarnings - totalPayouts;
  return <div className="space-y-6" data-id="element-1654">
      {/* Header */}
      <header className="flex items-center justify-between" data-id="element-1655">
        <div data-id="element-1656">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1657">
            Udbetalinger (Intern)
          </h1>
          <p className="text-stone-600" data-id="element-1658">
            Administrer dine interne udbetalinger og indtjeninger
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1659">
          <Button onClick={() => setIsAddEarningModalOpen(true)} variant="primary" className="flex items-center gap-2" data-id="element-1660">
            <PlusIcon className="w-5 h-5" data-id="element-1661" />
            Tilføj indtjening
          </Button>
          <Button onClick={() => setIsAddPayoutModalOpen(true)} variant="secondary" className="flex items-center gap-2" data-id="element-1662">
            <PlusIcon className="w-5 h-5" data-id="element-1663" />
            Tilføj udbetaling
          </Button>
        </div>
      </header>

      {/* Stats */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1664">
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1665">
          <div className="flex items-center gap-3 mb-2" data-id="element-1666">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1667">
              <TrendingUpIcon className="w-5 h-5 text-emerald-700" data-id="element-1668" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1669">
              Total indtjeninger
            </p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" data-id="element-1670">
            {totalEarnings.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1671">
            {earnings.length} indtjeninger
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1672">
          <div className="flex items-center gap-3 mb-2" data-id="element-1673">
            <div className="p-2 bg-red-100 rounded-lg" data-id="element-1674">
              <TrendingDownIcon className="w-5 h-5 text-red-700" data-id="element-1675" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1676">
              Total udbetalinger
            </p>
          </div>
          <p className="text-3xl font-bold text-red-700" data-id="element-1677">
            {totalPayouts.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1678">
            {payouts.length} udbetalinger
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1679">
          <div className="flex items-center gap-3 mb-2" data-id="element-1680">
            <div className={`p-2 rounded-lg ${netAmount >= 0 ? 'bg-teal-100' : 'bg-amber-100'}`} data-id="element-1681">
              <WalletIcon className={`w-5 h-5 ${netAmount >= 0 ? 'text-teal-700' : 'text-amber-700'}`} data-id="element-1682" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1683">Netto</p>
          </div>
          <p className={`text-3xl font-bold ${netAmount >= 0 ? 'text-teal-700' : 'text-amber-700'}`} data-id="element-1684">
            {netAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1685">
            Indtjeninger - Udbetalinger
          </p>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="relative" data-id="element-1686">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1687" />
        <input type="text" placeholder="Søg i noter, type eller kunde..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1688" />
      </motion.div>

      {/* Earnings Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-1689">
        <div className="flex items-center justify-between mb-4" data-id="element-1690">
          <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1691">
            Indtjeninger
          </h2>
          <span className="text-sm text-stone-500" data-id="element-1692">
            {filteredEarnings.length} indtjeninger
          </span>
        </div>

        {filteredEarnings.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1693">
            <TrendingUpIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1694" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1695">
              {searchQuery ? 'Ingen indtjeninger matcher din søgning' : 'Ingen indtjeninger endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1696">
              {searchQuery ? 'Prøv at justere din søgning' : 'Tilføj din første indtjening for at komme i gang'}
            </p>
            {!searchQuery && <Button onClick={() => setIsAddEarningModalOpen(true)} data-id="element-1697">
                Tilføj indtjening
              </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1698">
            {filteredEarnings.map((earning, index) => <EarningCard key={earning.id} earning={earning} index={index} onEdit={setEditingEarning} onDelete={onDeleteEarning} readOnly={isReadOnly} data-id="element-1699" />)}
          </div>}
      </motion.div>

      {/* Payouts Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} data-id="element-1700">
        <div className="flex items-center justify-between mb-4" data-id="element-1701">
          <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1702">
            Udbetalinger
          </h2>
          <span className="text-sm text-stone-500" data-id="element-1703">
            {filteredPayouts.length} udbetalinger
          </span>
        </div>

        {filteredPayouts.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1704">
            <WalletIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1705" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1706">
              {searchQuery ? 'Ingen udbetalinger matcher din søgning' : 'Ingen udbetalinger endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1707">
              {searchQuery ? 'Prøv at justere din søgning' : 'Tilføj din første udbetaling for at komme i gang'}
            </p>
            {!searchQuery && <Button onClick={() => setIsAddPayoutModalOpen(true)} variant="secondary" data-id="element-1708">
                Tilføj udbetaling
              </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1709">
            {filteredPayouts.map((payout, index) => <PayoutCard key={payout.id} payout={payout} index={index} onEdit={setEditingPayout} onDelete={onDeletePayout} readOnly={isReadOnly} data-id="element-1710" />)}
          </div>}
      </motion.div>

      <AddPayoutModal isOpen={isAddPayoutModalOpen} onClose={() => setIsAddPayoutModalOpen(false)} onAdd={onAddPayout} customers={customers} data-id="element-1711" />

      <EditPayoutModal isOpen={!!editingPayout} onClose={() => setEditingPayout(null)} onSave={onEditPayout} payout={editingPayout} customers={customers} data-id="element-1712" />

      <AddEarningModal isOpen={isAddEarningModalOpen} onClose={() => setIsAddEarningModalOpen(false)} onAdd={onAddEarning} customers={customers} data-id="element-1713" />

      <EditEarningModal isOpen={!!editingEarning} onClose={() => setEditingEarning(null)} onSave={onEditEarning} earning={editingEarning} customers={customers} data-id="element-1714" />
    </div>;
}